import React from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, FormProvider } from 'react-hook-form'
import { Button, Text } from '@asad/ui'

import { Modal } from '@/components'
import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks'
import { checkMyConsentExists } from '@/api/consent.api'
import { doAcceptConsent, doLogout, requireConsent } from '@/store/slices/auth-slice'
import { FormCheckboxField } from '@/components/forms/FormCheckboxField'
import { getActiveRodoPolicyData } from '@/store/slices/rodoPoliciesDocuments-slice'
import { getActiveDataProcessingConsentData } from '@/store/slices/dataProcessingConsents-slice'
import { getDownloadFilename } from '@/utils/utils'

type ConsentFormValues = {
  privacyPolicyAccepted: boolean
  dataProcessingAccepted: boolean
}
type ConsentGateProps = {
  children: React.ReactNode
}

export const ConsentGate = ({ children }: ConsentGateProps) => {
  const [submitting, setSubmitting] = React.useState(false)

  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const roles = useAppSelector((state) => state.profile?.data?.role || [])
  const consentRequired = useAppSelector((state) => state.auth.consentRequired)
  const activeRodoPolicy = useAppSelector((state) => state.rodoPoliciesDocuments.activeDocument)
  const activeDataProcessing = useAppSelector((state) => state.dataProcessingConsents.activeDocument)

  const isAdminRodo = roles.includes('ADMIN_RODO')

  const buildDocumentHref = React.useCallback((content?: string, type?: string): string | undefined => {
    if (!content) return undefined
    if (content.startsWith('data:')) return content

    const mime = type || 'application/pdf'

    return `data:${mime};base64,${content}`
  }, [])

  React.useEffect(() => {
    if (isAdminRodo) return
    const mountedRef = { current: true }

    ;(async () => {
      try {
        const [hasConsent] = await Promise.all([
          checkMyConsentExists(),
          dispatch(getActiveRodoPolicyData()),
          dispatch(getActiveDataProcessingConsentData()),
        ])

        if (!mountedRef.current) return

        if (!hasConsent) dispatch(requireConsent())
      } catch (err) {
        console.error('Failed to verify consent', err)
      }
    })()

    return () => {
      mountedRef.current = false
    }
  }, [dispatch, isAdminRodo])

  const methods = useForm<ConsentFormValues>({
    defaultValues: { privacyPolicyAccepted: false, dataProcessingAccepted: false },
  })

  const {
    handleSubmit,
    watch,
    formState: { errors },
  } = methods

  const privacyPolicyAccepted = watch('privacyPolicyAccepted')
  const dataProcessingAccepted = watch('dataProcessingAccepted')

  const handleAccept = handleSubmit(async (data) => {
    if (!data.privacyPolicyAccepted || !data.dataProcessingAccepted) return
    setSubmitting(true)
    try {
      await dispatch(
        doAcceptConsent({
          rodoPolicyDocumentId: activeRodoPolicy?.id,
          dataProcessingConsentId: activeDataProcessing?.id,
        }),
      ).unwrap()
    } finally {
      setSubmitting(false)
    }
  })

  const handleReject = () => {
    dispatch(doLogout())
  }
  const privacyPolicyUrl = buildDocumentHref(
    activeRodoPolicy?.documentationFile?.content,
    activeRodoPolicy?.documentationFile?.type,
  )
  const dataProcessingUrl = buildDocumentHref(
    activeDataProcessing?.documentationFile?.content,
    activeDataProcessing?.documentationFile?.type,
  )

  const shouldShowModal = !isAdminRodo && consentRequired && Boolean(activeRodoPolicy && activeDataProcessing)

  return (
    <>
      {children}
      <Modal
        isOpen={shouldShowModal}
        handleCloseModal={() => {}}
        title={t('consentGate.title')}
        isChart={false}
        hideClose
      >
        <p className='mb-5'>{t('consentGate.description')}</p>
        <FormProvider {...methods}>
          <form className='flex flex-col gap-4 mt-2' onSubmit={handleAccept}>
            <div className='flex flex-col gap-3'>
              <FormCheckboxField
                name='privacyPolicyAccepted'
                label={
                  <span className='text-sm'>
                    {t('consentGate.privacyLabelPrefix')}
                    <a
                      className='text-primary underline'
                      href={privacyPolicyUrl}
                      target='_blank'
                      rel='noreferrer noopener'
                      download={getDownloadFilename(activeRodoPolicy)}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      {t('consentGate.privacyLink')}
                    </a>
                  </span>
                }
                error={errors.privacyPolicyAccepted}
              />
              <FormCheckboxField
                name='dataProcessingAccepted'
                label={
                  <span className='text-sm'>
                    {t('consentGate.dataProcessingLabel')}
                    <a
                      className='text-primary underline'
                      href={dataProcessingUrl}
                      target='_blank'
                      rel='noreferrer noopener'
                      download={getDownloadFilename(activeDataProcessing)}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      {t('consentGate.dataProcessingLink')}
                    </a>
                  </span>
                }
                error={errors.dataProcessingAccepted}
              />
            </div>
            <Button
              size='mdFull'
              type='submit'
              disabled={!privacyPolicyAccepted || !dataProcessingAccepted || submitting}
              className='w-full justify-center'
            >
              {t('consentGate.acceptButton')}
            </Button>
            <Button
              size='mdFull'
              type='button'
              variant='secondary'
              onClick={handleReject}
              className='w-full justify-center'
            >
              {t('consentGate.rejectButton')}
            </Button>
            <Text className='text-xs text-tertiary-text'>{t('consentGate.helperText')}</Text>
          </form>
        </FormProvider>
      </Modal>
    </>
  )
}

export default ConsentGate
